import { LogLevel } from './LogLevel'
import type { LogRecord } from './types'

/** Worker 回传日志消息协议：主线程适配层识别 __log__ 字段转交 LoggerManager */
export interface WorkerLogMessage {
  /** 单条日志记录 */
  __log__: LogRecord
}

/** Worker 侧轻量 logger，无级别判断，所有记录无脑回传主线程，由主线程统一过滤 */
export interface WorkerLogger {
  /**
   * 回传 ERROR 级日志。
   *
   * @param msg - 已拼接的消息字符串
   */
  error(msg: string): void
  /**
   * 回传 WARN 级日志。
   *
   * @param msg - 已拼接的消息字符串
   */
  warn(msg: string): void
  /**
   * 回传 INFO 级日志。
   *
   * @param msg - 已拼接的消息字符串
   */
  info(msg: string): void
  /**
   * 回传 DEBUG 级日志。
   *
   * @param msg - 已拼接的消息字符串
   */
  debug(msg: string): void
  /**
   * 派生子命名空间 logger。
   *
   * @param sub - 子命名空间片段
   * @returns 子命名空间 WorkerLogger
   */
  child(sub: string): WorkerLogger
  /**
   * 同步计时，end 调用后以 DEBUG 级回传 `<ns>:perf` 耗时。
   *
   * @param label - 计时标签
   * @returns end 函数，调用后返回耗时毫秒数
   */
  time(label: string): () => number
}

/** Worker 时钟：优先 performance.now */
function workerNow(): number {
  const perf = (globalThis as { performance?: { now?: () => number } }).performance
  return perf != null && typeof perf.now === 'function' ? perf.now() : 0
}

/**
 * 判断一条 postMessage 数据是否为 Worker 日志回传消息。
 *
 * @param data - postMessage 收到的数据
 * @returns 是日志消息返回 true
 */
export function isLogMessage(data: unknown): data is WorkerLogMessage {
  return typeof data === 'object' && data !== null && '__log__' in (data as Record<string, unknown>)
}

/**
 * 创建 Worker 侧 logger，将日志记录通过 post 回传主线程。
 *
 * @param post - postMessage 包装函数（通常为 self.postMessage 绑定）
 * @param namespace - 命名空间，默认空串
 * @returns WorkerLogger 实例
 */
export function createWorkerLogger(
  post: (message: WorkerLogMessage) => void,
  namespace = ''
): WorkerLogger {
  const send = (level: LogLevel, message: string): void => {
    post({ __log__: { level, namespace, message, timestamp: workerNow() } })
  }
  return {
    error: (msg) => send(LogLevel.ERROR, msg),
    warn: (msg) => send(LogLevel.WARN, msg),
    info: (msg) => send(LogLevel.INFO, msg),
    debug: (msg) => send(LogLevel.DEBUG, msg),
    child: (sub) => createWorkerLogger(post, namespace === '' ? sub : `${namespace}:${sub}`),
    time: (label) => {
      const start = workerNow()
      return () => {
        const elapsed = workerNow() - start
        post({
          __log__: {
            level: LogLevel.DEBUG,
            namespace: namespace === '' ? 'perf' : `${namespace}:perf`,
            message: `${label} ${elapsed.toFixed(1)}ms`,
            timestamp: workerNow(),
          },
        })
        return elapsed
      }
    },
  }
}
